import { useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { bugsnagClient } from "../../../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../../../utils/analytics";
import api from "../../../utils/api";
import type { NotifiableError } from "@bugsnag/core/types/common";
import type { ChangePasswordFormData } from "./types";

const MINIMUM_PASSWORD_LENGTH = 6;

const INITIAL_FORM_DATA: ChangePasswordFormData = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export const ChangePasswordSection = () => {
  const { t } = useTranslation();

  const [formData, setFormData] =
    useState<ChangePasswordFormData>(INITIAL_FORM_DATA);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setError("");
  };

  const handleCancel = () => {
    setIsOpen(false);
    setFormData(INITIAL_FORM_DATA);
    setError("");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");

    if (formData.newPassword.length < MINIMUM_PASSWORD_LENGTH) {
      setError(t("profile.password.min_length"));
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t("profile.password.mismatch"));
      return;
    }

    setIsSaving(true);
    try {
      const data = await api.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      // Existing refresh tokens were revoked server-side, so persist the freshly
      // issued pair to keep the current session logged in after the change.
      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGED);
      setSuccessMessage(t("profile.password.success"));
      setFormData(INITIAL_FORM_DATA);
      setIsOpen(false);
    } catch (err) {
      bugsnagClient.notify(err as NotifiableError);
      setError((err as Error).message || t("profile.password.failed"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-4 p-6">
        <h2 className="text-lg font-semibold text-slate-900">
          {t("profile.password.title")}
        </h2>
        {!isOpen && (
          <button
            type="button"
            onClick={() => {
              setSuccessMessage("");
              setIsOpen(true);
            }}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 active:scale-[.99]"
          >
            {t("profile.password.change")}
          </button>
        )}
      </div>
      {successMessage && !isOpen && (
        <div className="mx-6 mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}
      {isOpen && (
        <>
          <div className="h-px w-full bg-slate-200" />
          {error && (
            <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid gap-5">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.password.current")}
                </span>
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.password.new")}
                </span>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  {t("profile.password.confirm")}
                </span>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-indigo-500 transition focus:ring-2"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[.99]"
              >
                {t("profile.password.cancel")}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 active:scale-[.99] disabled:cursor-not-allowed disabled:bg-indigo-400"
              >
                {t("profile.password.save")}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
